import {
  getInTabMorphemePanelsHref,
  MorphemeInfoPanelContext,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content._context";
import { MorphemeGlyphTileGridView } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.link.glyphs.list";
import { useBatchedGraphemeTileData } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.glyphs";
import { Separator } from "@-/dev.improve.haniverse.components.ui.separator";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { View } from "@-/com.npmjs.react-native";
import type { MorphemeInfoPanelDictionarySimilarSectionGate } from "#use-morpheme-info-panel-dictionary-similar-section-gate";

// Learn's card surfaces (the deck) and reference pages live under `/learn/`. A Similar tap from
// there stays in the Learn tab: it pushes the Learn reference route for the tapped grapheme ABOVE
// the deck, so going back returns to the card being studied with its rating target intact.
const LEARN_HREF_SUBSTRING = "/learn/";

// This row lives in the Overview's fixed-width left column (`w-29.5` in
// `morpheme-info-panel-dictionary-morpheme-section.tsx`), ~100dp inside its padding. The grid's
// default 8 columns crushed each tile to ~8dp there — too small to read and, on Android, to tap
// (staging run 35822751809: `review-card-back-to-review` tapped 夫's 8x8 tile and nothing opened).
// Two columns keep the ~40dp tiles this row had before it moved onto the shared grid.
const SIMILAR_GRID_COLUMNS = 2;

/** The Overview's "Similar" row. Only `useMorphemeInfoPanelDictionarySimilarSectionGate` decides whether it shows. */
export function MorphemeInfoPanelDictionarySimilarSection({
  gate,
}: {
  gate: MorphemeInfoPanelDictionarySimilarSectionGate;
}) {
  const commonSimilarGraphemeList = gate.graphemeList;
  const [morphemeHrefObject] = useContextSelectors(
    MorphemeInfoPanelContext,
    (c) => c.morphemeHrefObject,
  );

  // Open the tapped card to the panel the user is currently on: the Mnemonics panel when the
  // Similar row is viewed from a Learn card (its back is the mnemonics), the Info panel from
  // Browse/Search.
  const isLearnContext = morphemeHrefObject.href.includes(LEARN_HREF_SUBSTRING);
  const similarCardView = isLearnContext ? "mnemonics" : "info";

  // ONE stage + pinyin-slice read for the row instead of one per similar tile. This section sits in
  // the info panel of every lesson/review card, so its tiles were re-reading both actor overlays
  // on each rating; handing both props down keeps them on the query-free branch.
  const tileData = useBatchedGraphemeTileData(commonSimilarGraphemeList);

  return (
    <>
      <Separator className="my-1.5 w-full" />
      <View className="flex-col gap-1">
        <Text className="text-xs text-muted-foreground">Similar</Text>
        <MorphemeGlyphTileGridView
          columns={SIMILAR_GRID_COLUMNS}
          graphemeList={commonSimilarGraphemeList}
          href={getInTabMorphemePanelsHref(morphemeHrefObject.href)}
          tileData={tileData}
          view={similarCardView}
        />
      </View>
    </>
  );
}
